import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, BadgeCheck, GitCompare, Check, Loader2 } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useTools } from '../hooks/useData';
import { categories } from '../hooks/useData';
import { useCompare } from '../context/CompareContext';
import { toast } from 'sonner';

const CategoryPage = () => {
  const { slug } = useParams();
  const { tools, loading } = useTools();
  const category = categories.find((c) => c.slug === slug);
  const categoryTools = tools.filter((t) => t.categorySlug === slug);
  const { toggleCompare, isInCompare, compareCount } = useCompare();

  const handleToggleCompare = (e, tool) => {
    e.preventDefault();
    e.stopPropagation();
    const result = toggleCompare(tool);
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.message);
    }
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 text-center py-20">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Category not found
            </h1>
            <Button asChild>
              <Link to="/categories">Browse All Categories</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="mb-6 flex items-center justify-between">
            <Link
              to="/categories"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              All Categories
            </Link>
            {compareCount > 0 && (
              <Link to="/compare">
                <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="category-compare-btn">
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare ({compareCount})
                </Button>
              </Link>
            )}
          </div>

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
            <div
              className={`w-16 h-16 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-6`}
            >
              <span className="text-2xl text-white">📦</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {category.name}
            </h1>
            <p className="text-lg text-gray-600 mb-4">{category.description}</p>
            <p className="text-sm text-gray-500">
              {categoryTools.length} tools in this category
            </p>
          </div>

          {/* Tools Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20 bg-white rounded-2xl border border-gray-100">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              <span className="ml-2 text-gray-600">Loading tools...</span>
            </div>
          ) : categoryTools.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryTools.map((tool) => (
                <div
                  key={tool.id}
                  className="group bg-white rounded-2xl border border-gray-100 p-5 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-100/50 transition-all duration-300 relative"
                >
                  <Link to={`/tool/${tool.slug}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden">
                        <img
                          src={tool.logo}
                          alt={tool.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/100?text=' + tool.name.charAt(0);
                          }}
                        />
                      </div>
                      {tool.verified && (
                        <Badge
                          variant="secondary"
                          className="bg-emerald-50 text-emerald-600 border-0"
                        >
                          <BadgeCheck className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">
                      {tool.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                      {tool.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {tool.rating}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({(tool.reviews || 0).toLocaleString()})
                        </span>
                      </div>
                      <span className="text-sm font-medium text-emerald-600">
                        {tool.pricing}
                      </span>
                    </div>
                  </Link>

                  {/* Add to Compare Button */}
                  <button
                    onClick={(e) => handleToggleCompare(e, tool)}
                    className={`absolute top-4 right-4 p-2 rounded-lg transition-all ${
                      isInCompare(tool.id)
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-100 text-gray-500 hover:bg-emerald-100 hover:text-emerald-600 opacity-0 group-hover:opacity-100'
                    }`}
                    title={isInCompare(tool.id) ? 'Remove from Compare' : 'Add to Compare'}
                    data-testid={`category-compare-btn-${tool.slug}`}
                  >
                    {isInCompare(tool.id) ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <GitCompare className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No tools found
              </h3>
              <p className="text-gray-500">Check back soon for new additions!</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;
